import { useState, useEffect, RefObject } from 'react';

interface UseReaderProgressOptions {
  offset?: number;
  throttleMs?: number;
}

/**
 * Hook để theo dõi tiến độ đọc của một phần tử có thể cuộn
 * @param ref Tham chiếu đến phần tử cần theo dõi
 * @param options Tùy chọn cấu hình (offset, throttleMs)
 * @returns Object chứa progress (0-100) và hàm startTracking
 */
export const useReaderProgress = (
  ref: RefObject<HTMLElement>,
  options: UseReaderProgressOptions = {}
) => {
  const { offset = 0, throttleMs = 100 } = options;
  const [progress, setProgress] = useState(0);
  const [isTracking, setIsTracking] = useState(false);

  // Hàm tính toán tiến độ đọc
  const calculateProgress = () => {
    if (!ref.current) return;
    
    const element = ref.current;
    const windowHeight = window.innerHeight;
    const documentHeight = element.scrollHeight;
    const scrollTop = element.scrollTop;
    
    const totalScrollDistance = documentHeight - windowHeight - offset;
    const currentScrollPosition = scrollTop;
    
    if (totalScrollDistance > 0) {
      const calculatedProgress = (currentScrollPosition / totalScrollDistance) * 100;
      setProgress(Math.min(Math.max(calculatedProgress, 0), 100));
    }
  };

  // Bắt đầu theo dõi tiến độ
  const startTracking = () => {
    setIsTracking(true);
  };

  // Thiết lập theo dõi sự kiện scroll
  useEffect(() => {
    const element = ref.current;
    if (!element || !isTracking) return;

    // Tạo throttled handler để tránh quá nhiều cập nhật
    let throttleTimeout: ReturnType<typeof setTimeout> | null = null;
    
    const handleScroll = () => {
      if (throttleTimeout) return;
      
      throttleTimeout = setTimeout(() => {
        calculateProgress();
        throttleTimeout = null;
      }, throttleMs);
    };

    // Tính toán ban đầu
    calculateProgress();
    
    // Thêm event listener
    element.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', calculateProgress);
    
    // Cleanup function
    return () => {
      element.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', calculateProgress);
      if (throttleTimeout) clearTimeout(throttleTimeout);
    };
  }, [ref, isTracking, throttleMs, offset]);

  return { progress, startTracking };
}; 